import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { getPaperById } from '@/lib/api';

/**
 * Hook for prefetching paper data on hover
 * Implements a 150ms delay to avoid prefetching on quick mouse movements
 */
export const usePaperPrefetch = () => {
  const queryClient = useQueryClient();
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  const prefetchPaper = useCallback((paperId: string) => {
    // Clear any existing timer
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
    }

    // Start prefetch after 150ms hover delay
    hoverTimerRef.current = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: ['paper', paperId],
        queryFn: () => getPaperById(paperId),
        staleTime: 1000 * 60 * 5, // 5 minutes
      });
    }, 150);
  }, [queryClient]);

  const cancelPrefetch = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  return { prefetchPaper, cancelPrefetch };
};
